import React from "react";
import { ArrowLeft, ShieldCheck, RefreshCcw, Truck, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGame } from "@/context/GameContext";

export default function TermsConditions() {
  const navigate = useNavigate();
  const { language } = useGame();

  const isIndo = language === "id";

  return (
    <div
      className="min-h-screen p-4 sm:p-8"
      style={{
        background: "linear-gradient(180deg, #FDFBF7 0%, #FEF6E4 100%)",
      }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/")}
            className="toy-btn w-12 h-12 rounded-full bg-white shadow-[0_4px_0_rgba(0,0,0,0.15)] flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" strokeWidth={3} />
          </button>
          <h1 className="font-display text-3xl font-bold text-slate-800">
            📜 {isIndo ? "Syarat & Ketentuan" : "Terms & Conditions"}
          </h1>
        </div>

        <Card className="rounded-3xl bg-white shadow-xl border-0 overflow-hidden mb-8">
          <CardHeader className="bg-slate-50 p-8 border-b border-slate-100">
            <CardTitle className="text-slate-800">
              {isIndo ? "Ketentuan Penggunaan & Kebijakan Transaksi" : "Terms of Use & Transaction Policy"}
            </CardTitle>
            <p className="text-slate-500 text-sm font-bold">
              {isIndo ? "Terakhir diperbarui: 19 Juni 2026" : "Last updated: June 19, 2026"}
            </p>
          </CardHeader>
          <CardContent className="p-8 prose prose-slate max-w-none">
            
            {/* Compliance Badges for Midtrans Onboarding */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="flex flex-col items-center p-3 bg-emerald-50 rounded-2xl border border-emerald-100">
                    <ShieldCheck className="w-6 h-6 text-emerald-600 mb-2" />
                    <span className="text-[10px] font-bold text-emerald-800 text-center uppercase">{isIndo ? "Data Terlindungi" : "Data Protected"}</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-sky-50 rounded-2xl border border-sky-100">
                    <RefreshCcw className="w-6 h-6 text-sky-600 mb-2" />
                    <span className="text-[10px] font-bold text-sky-800 text-center uppercase">{isIndo ? "Kebijakan Refund" : "Refund Policy"}</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-amber-50 rounded-2xl border border-amber-100">
                    <Truck className="w-6 h-6 text-amber-600 mb-2" />
                    <span className="text-[10px] font-bold text-amber-800 text-center uppercase">{isIndo ? "Akses Instan" : "Instant Access"}</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-indigo-50 rounded-2xl border border-indigo-100">
                    <Lock className="w-6 h-6 text-indigo-600 mb-2" />
                    <span className="text-[10px] font-bold text-indigo-800 text-center uppercase">{isIndo ? "Sertifikasi SSL" : "SSL Certified"}</span>
                </div>
            </div>

            {isIndo ? (
              <div className="space-y-8 text-slate-600 font-bold text-sm leading-relaxed">
                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">1. Informasi Umum & Produk</h3>
                  <p>Platform Neotunas adalah layanan produk digital berupa langganan akses game edukasi. Neotunas dimiliki dan dikelola oleh BabyLabs yang berkedudukan di Indonesia.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">2. Kebijakan Privasi (Privacy Policy)</h3>
                  <p>Kami menjamin keamanan data transaksi dan privasi pengguna. Sesuai dengan UU PDP, data anak hanya digunakan untuk keperluan gamifikasi internal dan data orang tua (email) digunakan untuk keperluan autentikasi dan bukti transaksi. Data kartu kredit atau informasi sensitif lainnya tidak kami simpan, melainkan diproses secara aman melalui Payment Gateway (Midtrans).</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">3. Ketentuan Pembayaran</h3>
                  <p>Seluruh transaksi dilakukan dalam mata uang Rupiah (IDR). Pembayaran dianggap sah jika status transaksi telah "Settlement" pada sistem Payment Gateway kami. Harga yang tertera sudah termasuk pajak yang berlaku.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">4. Kebijakan Pengiriman (Delivery Policy)</h3>
                  <p>Karena Neotunas adalah <strong>Produk Digital</strong>, tidak ada pengiriman fisik. Akses fitur Premium (Research Mode & Seluruh Layer) akan terbuka secara otomatis dan instan segera setelah pembayaran berhasil dikonfirmasi oleh sistem.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">5. Kebijakan Pengembalian Dana (Refund Policy)</h3>
                  <p>Kami memberlakukan kebijakan pengembalian dana dalam kondisi tertentu:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Pengembalian dana hanya dapat diajukan jika terjadi kesalahan teknis yang menyebabkan akses fitur tetap terkunci setelah pembayaran berhasil.</li>
                    <li>Permintaan refund harus diajukan maksimal 2x24 jam setelah transaksi melalui email ke <u>neotunas.id@gmail.com</u> dengan melampirkan bukti bayar.</li>
                    <li>Proses refund akan dilakukan secara manual dalam waktu 7-14 hari kerja melalui metode transfer bank.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">6. Batasan Tanggung Jawab</h3>
                  <p>Neotunas menyediakan stimulasi edukasi berbasis riset. Kami tidak bertanggung jawab atas keputusan medis yang diambil pengguna berdasarkan data ekspor sistem kami tanpa berkonsultasi dengan tenaga profesional.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">7. Hubungi Kami & Layanan Pelanggan</h3>
                  <p>Jika Anda memiliki pertanyaan seputar Syarat & Ketentuan, Kendala Pembayaran, atau Pengajuan Refund, silakan hubungi tim dukungan kami:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Pengelola / Pengembang:</strong> BabyLabs Indonesia</li>
                    <li><strong>Email Layanan Pelanggan:</strong> <a href="mailto:neotunas.id@gmail.com" className="text-sky-600 underline">neotunas.id@gmail.com</a></li>
                    <li><strong>Situs Resmi:</strong> <a href="https://neotunas.centonk.my.id" className="text-sky-600 underline">https://neotunas.centonk.my.id</a></li>
                    <li><strong>Jam Operasional CS:</strong> Senin - Jumat, 09.00 - 17.00 WIB</li>
                  </ul>
                </section>
              </div>
            ) : (
              <div className="space-y-8 text-slate-600 font-bold text-sm leading-relaxed">
                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">1. General Information & Products</h3>
                  <p>Neotunas platform is a digital product service for educational game subscriptions. Neotunas is owned and managed by BabyLabs, based in Indonesia.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">2. Privacy Policy</h3>
                  <p>We guarantee transaction security and user privacy. In accordance with PDP laws, child data is only used for internal gamification, and parent data (email) is used for authentication and proof of transaction. We do not store credit card data or other sensitive information; these are processed securely through our Payment Gateway (Midtrans).</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">3. Payment Terms</h3>
                  <p>All transactions are conducted in Indonesian Rupiah (IDR). Payments are considered valid once the transaction status is "Settlement" in our Payment Gateway system. Listed prices are inclusive of applicable taxes.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">4. Delivery Policy</h3>
                  <p>As Neotunas is a <strong>Digital Product</strong>, there is no physical shipping. Access to Premium features (Research Mode & All Layers) will be unlocked automatically and instantly once payment is successfully confirmed by the system.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">5. Refund Policy</h3>
                  <p>We apply a refund policy under certain conditions:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Refunds can only be requested if a technical error occurs where features remain locked after a successful payment.</li>
                    <li>Refund requests must be submitted within 2x24 hours after the transaction via email to <u>neotunas.id@gmail.com</u> with proof of payment attached.</li>
                    <li>Refund processing will be done manually within 7-14 business days via bank transfer.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">6. Limitation of Liability</h3>
                  <p>Neotunas provides research-based educational stimulation. We are not responsible for medical decisions made by users based on system data exports without consulting a professional.</p>
                </section>

                <section>
                  <h3 className="text-slate-800 font-display text-lg border-b pb-2 mb-4">7. Contact Us & Customer Support</h3>
                  <p>If you have any questions regarding these Terms, Payment Issues, or Refund Requests, please contact our support team:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Managed By:</strong> BabyLabs Indonesia</li>
                    <li><strong>Customer Support Email:</strong> <a href="mailto:neotunas.id@gmail.com" className="text-sky-600 underline">neotunas.id@gmail.com</a></li>
                    <li><strong>Official Website:</strong> <a href="https://neotunas.centonk.my.id" className="text-sky-600 underline">https://neotunas.centonk.my.id</a></li>
                    <li><strong>Support Hours:</strong> Monday - Friday, 09:00 - 17:00 WIB</li>
                  </ul>
                </section>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-slate-400 text-xs font-bold pb-8">
          &copy; 2026 Neotunas by BabyLabs. All rights reserved.
        </div>
      </div>
    </div>
  );
}
